import { Box, useMediaQuery, useTheme } from "@mui/material";
import { EditorContent, useEditor } from "@tiptap/react";
import { useFormikContext } from "formik";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { setCompetitorAnalysis } from "../../../../redux/slices/competitorSlice";
import {
  setActivePanel,
  setCurrentZone,
} from "../../../../redux/slices/panelSlice";
import { useGetLinkTableMutation } from "../../../../redux/slices/postEditorBlogSlice";
import { dispatch } from "../../../../redux/store";
import { removeSpanTags } from "../../helper";
import { getLinks } from "../../seoAnalyzer/linkingHelper";
import { EditorContext } from "./EditorContext";
import "./MainEditor.scss";
import MobileSidePanel from "./SidePanel/MobileSidePanel";
import Outline from "./SidePanel/Outline";
import DesktopSidePanel from "./SidePanel/SidePanel";
import UrlSlug from "./SidePanel/UrlSlug";
import Content from "./SidePanel/content-panel";
import H1Title from "./SidePanel/h1-title-panel";
import Linking from "./SidePanel/linking-panel";
import Medias from "./SidePanel/medias-panel";
import Metadata from "./SidePanel/metadata/metadata-panel";
import Semantics from "./SidePanel/semantics-panel";
import Toolbar from "./Toolbar";
import { WebSocketProvider } from "./WebSocketContext";
import { isHeadingZone } from "./Zones/HeadingZones/H2Zone/Extension";
import ConclusionBubbleMenu from "./bubble-menus/conclusion-menu/conclusion-bubble-menu";
import IntroZoneBubbleMenu from "./bubble-menus/introduction-menu/introzone-bubble-menu";
import PartBubbleMenu from "./bubble-menus/part-bubble-menu/part-bubble-menu";
import SynonymBubbleMenu from "./bubble-menus/synonymns-bubble-menu/synonyms-bubble-menu";
import { useProject } from "./projectContext";

const loadFormikValues = (values) => {
  const h1zone = document.getElementById("h1zone");
  const introzone = document.getElementById("introzone");
  const contentzone = document.getElementById("contentzone");
  const conclusionzone = document.getElementById("conclusionzone");

  if (h1zone) {
    h1zone.textContent = values.h1_title;
  }
  if (introzone) {
    introzone.innerHTML = values.introduction;
  }
  if (contentzone) {
    contentzone.innerHTML = values.content;
  }
  if (conclusionzone) {
    conclusionzone.innerHTML = values.conclusion;
  }
};

const MainEditor = ({ content, extensions }) => {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext();
  const h1zone = document.getElementById("h1zone");
  const introzone = document.getElementById("introzone");
  const contentzone = document.getElementById("contentzone");
  const conclusionzone = document.getElementById("conclusionzone");
  const [characterCount, setCharacterCount] = useState(0);
  const [wordCount, setWordCount] = useState(0);

  const theme = useTheme();
  const isLarge = useMediaQuery(theme.breakpoints.up("md"));
  const competitorResults = useSelector(
    (state) => state.competitorAnalysis.data?.data
  );
  const [linkingTable, { isLoading: loadingTable }] = useGetLinkTableMutation();
  const [nb_links, setNb_links] = useState(0);
  useEffect(() => {
    const tags = Array.from(
      getLinks(
        values.introduction.trim() +
          values.content.trim() +
          values.conclusion.trim()
      )
    ).filter(
      (link) =>
        link.getAttribute("href") &&
        link.getAttribute("href").trim() !== "" &&
        !link.getAttribute("href").startsWith("#")
    );

    if (nb_links != tags.length) {
      setNb_links(tags.length);
      loadLinkingTable();
    }
  }, [values]);

  const loadLinkingTable = async () => {
    if (competitorResults) {
      const payload = {
        text: values.introduction + values.content + values.conclusion,
        domain: values.url_path,
      };
      const res = await linkingTable(payload).unwrap();
      if (res?.table) {
        dispatch(
          setCompetitorAnalysis({
            data: {
              ...competitorResults,
              linkingTable: res,
            },
          })
        );
      }
    }
  };

  const editor = useEditor({
    extensions: extensions,
    content: content,
    onCreate: () => {
      loadFormikValues(values);
      updateCounts(); // Initialize word and character count on creation
    },

    onUpdate: () => {
      // handleHighlights();
      if (h1zone) {
        setFieldValue("h1_title", h1zone.textContent);
      }
      if (introzone) {
        const d = introzone.textContent !== "" ? introzone.innerHTML : "";

        setFieldValue("introduction", removeSpanTags(d));
      }
      if (contentzone) {
        const d = contentzone.textContent !== "" ? contentzone.innerHTML : "";
        setFieldValue("content", removeSpanTags(d));
      }
      if (conclusionzone) {
        const d =
          conclusionzone.textContent !== "" ? conclusionzone.innerHTML : "";
        setFieldValue("conclusion", removeSpanTags(d));
      }

      updateCounts(); // Update word and character count on content change
    },
  });

  const updateCounts = () => {
    const textContent = editor?.getText().trim() || "";
    setCharacterCount(textContent.length);
    setWordCount(
      textContent.split(/\s+/).filter((word) => word.length > 0).length
    );
  };

  const getCurrentZone = (node) => {
    if (!node) return null;
    if (node?.parent?.name === "doc") return node.name;
    return getCurrentZone(node.parent);
  };

  useEffect(() => {
    const node = editor?.$pos(editor?.state?.selection.head);
    const zone = getCurrentZone(node);
    const headingZone = isHeadingZone(node)?.element ?? null;

    dispatch(setCurrentZone({ zone: zone, isHeadingZone: headingZone }));
  }, [editor?.state?.selection?.head, values]);

  useEffect(() => {
    if (h1zone) {
      h1zone.addEventListener("mousedown", () => {
        dispatch(setActivePanel("h1Title"));
      });
    }
  }, [h1zone]);

  // UPDATE BLOG POST WITH PROJECT INFOS
  const { project } = useProject();
  useEffect(() => {
    if (!values["url_path"] && project?.export_data?.blog_url) {
      setFieldValue("url_path", project?.export_data?.blog_url);
    }
  }, [project]);
  useEffect(() => {
    if (competitorResults?.linkingTable == undefined) {
      loadLinkingTable();
    }
  }, [competitorResults]);

  return editor ? (
    <EditorContext.Provider value={editor}>
      <WebSocketProvider editor={editor}>
        <Box
          sx={{
            width: {
              lg: "calc(100vw - 500px)",
              md: "calc(100vw - 350px)",
              xs: "100%",
            },
            position: "relative",
            display: "flex",
            flexDirection: "column",
            height: "100vh",
          }}
          className="post-editor-container"
        >
          <Toolbar editor={editor} />
          <SynonymBubbleMenu editor={editor} />
          <IntroZoneBubbleMenu editor={editor} />
          <ConclusionBubbleMenu editor={editor} />
          <PartBubbleMenu editor={editor} />

          <EditorContent className="editor-wrapper" editor={editor} />

          <div className="text-sm ml-[16px] mt-2">
            {wordCount} {t("words-count", "mots")} {characterCount}{" "}
            {t("characters", "caractères")}
          </div>
        </Box>
        {isLarge ? <DesktopSidePanel /> : <MobileSidePanel />}
        <Panels />
      </WebSocketProvider>
    </EditorContext.Provider>
  ) : null;
};

const Panels = () => {
  const panelsMap = {
    outline: <Outline />,
    urlSlug: <UrlSlug />,
    h1Title: <H1Title />,
    content: <Content />,
    metaData: <Metadata />,
    semantics: <Semantics />,
    mesh: <Linking />,
    medias: <Medias />,
  };

  const activePanel = useSelector((state) => state.panelSlice.activePanel);

  return <>{panelsMap[activePanel] ?? null}</>;
};

export default MainEditor;
